import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, Save, ListChecks, AlertCircle, Sparkles, Loader2, LogOut } from "lucide-react";

/**
 * Cuestionario premium de 35 preguntas (37 si el equipo es multicultural).
 * - Autoguardado cada 3 s.
 * - Mini-índice por áreas cliqueable con % completado.
 * - Contador de palabras en preguntas abiertas.
 * - Atajos de teclado (← → 1-9).
 * - Celebración con confetti a los 25/50/75/100%.
 * - Pantalla de revisión pre-submit.
 */
export default function ConsultingTest() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(0);
  const [savingState, setSavingState] = useState("saved");
  const [submitting, setSubmitting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const dirtyRef = useRef(false);
  const pendingRef = useRef({});
  const lastPctRef = useRef(0);
  const initialLoadRef = useRef(true);

  useEffect(() => {
    Promise.all([
      api.get(`/consulting/questions?test_id=${encodeURIComponent(testId)}`),
      api.get(`/consulting/tests/${testId}`),
    ])
      .then(([q, t]) => {
        setAreas(q.data.areas);
        setQuestions(q.data.questions);
        setTest(t.data);
        setAnswers(t.data.answers || {});
        if (t.data.status !== "draft") {
          toast.message("Este test ya se envió");
          navigate("/consulting");
        }
      })
      .catch(() => {
        toast.error("No pudimos cargar el cuestionario");
        navigate("/consulting");
      });
  }, [testId, navigate]);

  // Autoguardado cada 3s
  useEffect(() => {
    const iv = setInterval(async () => {
      if (!dirtyRef.current) return;
      const patch = { ...pendingRef.current };
      pendingRef.current = {};
      dirtyRef.current = false;
      setSavingState("saving");
      try {
        await api.patch(`/consulting/tests/${testId}`, { answers: patch });
        setSavingState("saved");
      } catch {
        pendingRef.current = { ...patch, ...pendingRef.current };
        dirtyRef.current = true;
        setSavingState("idle");
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [testId]);

  const q = questions[current];
  const mandatoryQuestions = useMemo(
    () => questions.filter((qq) => !qq.optional),
    [questions]
  );
  const totalAnswered = useMemo(
    () => Object.values(answers).filter((v) => v && String(v.value ?? "").trim()).length,
    [answers]
  );
  const mandatoryAnswered = useMemo(
    () => mandatoryQuestions.filter((qq) => {
      const ans = answers[qq.n];
      return ans && String(ans.value ?? "").trim();
    }).length,
    [mandatoryQuestions, answers]
  );
  const answeredPct = mandatoryQuestions.length > 0 ? Math.round((mandatoryAnswered / mandatoryQuestions.length) * 100) : 0;

  // Progreso por área
  const areaProgress = useMemo(() => {
    if (!questions.length) return [];
    return areas.map((a) => {
      const questionsOfArea = questions.filter((qq) => qq.area === a.code);
      const done = questionsOfArea.filter((qq) => {
        const ans = answers[qq.n];
        return ans && String(ans.value ?? "").trim();
      }).length;
      const firstN = questionsOfArea[0]?.n;
      return { ...a, total: questionsOfArea.length, done, firstN, pct: questionsOfArea.length > 0 ? Math.round((done / questionsOfArea.length) * 100) : 0 };
    });
  }, [areas, questions, answers]);

  const setAnswer = useCallback((n, value, type) => {
    setAnswers((prev) => ({ ...prev, [n]: { value, type } }));
    pendingRef.current[n] = { value, type };
    dirtyRef.current = true;
    setSavingState("idle");
  }, []);

  // Celebración en hitos (skip initial mount)
  useEffect(() => {
    if (!questions.length) return;
    if (initialLoadRef.current) {
      lastPctRef.current = answeredPct;
      initialLoadRef.current = false;
      return;
    }
    const milestones = [25, 50, 75, 100];
    for (const m of milestones) {
      if (lastPctRef.current < m && answeredPct >= m) {
        confetti({
          particleCount: m === 100 ? 220 : 90,
          spread: 78,
          origin: { y: 0.6 },
          colors: ["#C9A84C", "#E5C874", "#0F6E56", "#0b3d2e"],
          scalar: 1.05,
        });
        if (m === 100) {
          toast.success("🎉 ¡Terminaste el cuestionario!", {
            description: "Revisa antes de enviar tu consultoría.",
            duration: 6500,
            action: { label: "Revisar y enviar", onClick: () => setReviewOpen(true) },
          });
        } else {
          const msgs = { 25: "¡Buen ritmo!", 50: "¡Vas por la mitad!", 75: "¡Casi lo tienes!" };
          toast.success(`${msgs[m]} · ${m}%`, { description: "Sigue así, la calidad del informe depende de tu detalle.", duration: 3500 });
        }
      }
    }
    lastPctRef.current = answeredPct;
  }, [answeredPct, questions.length]);

  // Atajos de teclado
  useEffect(() => {
    if (!q || reviewOpen) return;
    function onKey(e) {
      if (e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT") {
        if (e.key === "ArrowLeft" && e.ctrlKey) {
          e.preventDefault();
          setCurrent((i) => Math.max(0, i - 1));
        } else if (e.key === "ArrowRight" && e.ctrlKey) {
          e.preventDefault();
          setCurrent((i) => Math.min(questions.length - 1, i + 1));
        }
        return;
      }
      if (e.key === "ArrowLeft") { e.preventDefault(); setCurrent((i) => Math.max(0, i - 1)); }
      else if (e.key === "ArrowRight") { e.preventDefault(); setCurrent((i) => Math.min(questions.length - 1, i + 1)); }
      else if (q.type === "multiple" && /^[1-9]$/.test(e.key)) {
        const idx = parseInt(e.key, 10) - 1;
        if (idx < (q.options || []).length) {
          e.preventDefault();
          setAnswer(q.n, idx, "multiple");
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [q, questions.length, reviewOpen, setAnswer]);

  async function goSubmit() {
    if (dirtyRef.current) {
      try {
        await api.patch(`/consulting/tests/${testId}`, { answers: pendingRef.current });
        pendingRef.current = {};
        dirtyRef.current = false;
      } catch {}
    }
    setSubmitting(true);
    try {
      await api.post(`/consulting/tests/${testId}/submit`);
      toast.success("Test enviado. Tu informe se está generando.");
      navigate(`/consulting/${testId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos enviar el test");
    } finally {
      setSubmitting(false);
    }
  }

  function goToQuestion(n) {
    const idx = questions.findIndex((qq) => qq.n === n);
    if (idx >= 0) {
      setCurrent(idx);
      setReviewOpen(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  if (!q) {
    return (
      <div className="min-h-screen py-12 px-4 md:px-6" style={{ background: "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)" }} data-testid="consulting-test-skeleton">
        <div className="max-w-3xl mx-auto">
          <div className="skeleton-pulse h-4 w-40 rounded mb-4" />
          <div className="skeleton-pulse h-2 w-full rounded mb-8" />
          <div className="skeleton-pulse rounded-3xl h-96" />
        </div>
      </div>
    );
  }

  const areaObj = areas.find((a) => a.code === q.area);
  const areaName = areaObj?.name || q.area;
  const answered = answers[q.n];
  const value = answered?.value ?? (q.type === "multiple" ? -1 : "");
  const wordCount = q.type === "open" ? (String(value || "").match(/\S+/g) || []).length : 0;

  return (
    <div
      className="min-h-screen py-8 md:py-12 px-4 md:px-6 overflow-x-hidden"
      style={{
        background:
          "radial-gradient(1200px 500px at 100% -10%, rgba(15,110,86,0.06) 0%, rgba(15,110,86,0) 60%)," +
          "radial-gradient(900px 400px at -10% 20%, rgba(201,168,76,0.06) 0%, rgba(201,168,76,0) 55%)," +
          "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)",
      }}
      data-testid="consulting-test"
    >
      <div className="max-w-3xl mx-auto">
        {/* Header con contexto empresa */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: "#94a3b8" }}>
              Consultoría Premium
            </div>
            {test?.company_name && (
              <div className="font-display text-base md:text-lg font-bold text-slate-900 truncate">
                {test.company_name}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <div className="text-xs text-slate-500 flex items-center gap-1.5 font-medium" data-testid="consulting-save-state">
              {savingState === "saving" ? (
                <><Save className="w-3.5 h-3.5 animate-pulse" /> Guardando…</>
              ) : (
                <><Check className="w-3.5 h-3.5" style={{ color: "#0F6E56" }} /> Guardado</>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                // El autosave ya persiste cada respuesta; salir es seguro. Damos
                // aviso claro al usuario de que su progreso queda guardado.
                toast.success("Progreso guardado", {
                  description: "Puedes retomar el test cuando quieras desde /consulting",
                  duration: 3000,
                });
                navigate("/consulting");
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:border-slate-400 hover:bg-slate-50 transition"
              data-testid="consulting-test-exit-btn"
              title="Salir del test — tu progreso queda guardado"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={2.25} />
              Salir del test
            </button>
          </div>
        </div>

        {/* Mini-índice por áreas cliqueable */}
        <div
          className="rounded-2xl p-3 mb-4"
          style={{ background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)", boxShadow: "0 6px 18px -14px rgba(11,61,46,0.15), 0 0 0 1px rgba(11,61,46,0.06)" }}
          data-testid="consulting-test-area-index"
        >
          <div className="grid grid-cols-3 md:grid-cols-6 gap-1.5">
            {areaProgress.map((a) => {
              const isCurrent = q.area === a.code;
              return (
                <button
                  key={a.code}
                  onClick={() => goToQuestion(a.firstN)}
                  className="text-left rounded-lg px-2 py-1.5 transition-all hover:-translate-y-0.5"
                  style={{
                    background: isCurrent ? "linear-gradient(135deg, rgba(15,110,86,0.10) 0%, rgba(201,168,76,0.10) 100%)" : "transparent",
                    border: isCurrent ? "1px solid rgba(15,110,86,0.35)" : "1px solid transparent",
                  }}
                  data-testid={`consulting-test-area-${a.code}`}
                  title={`${a.name}: ${a.done}/${a.total}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-bold uppercase" style={{ color: isCurrent ? "#0b3d2e" : "#0F6E56" }}>{a.code}</span>
                    <span className="text-[10px] font-bold tabular-nums" style={{ color: a.pct === 100 ? "#C9A84C" : "#94a3b8" }}>{a.done}/{a.total}</span>
                  </div>
                  <div className="text-[10px] font-medium truncate mb-1" style={{ color: isCurrent ? "#0b3d2e" : "#64748b" }}>{a.name}</div>
                  <div className="h-1 rounded-full overflow-hidden" style={{ background: "#eef2f6" }}>
                    <div className="h-full transition-all duration-500" style={{ width: `${a.pct}%`, background: a.pct === 100 ? "linear-gradient(90deg, #C9A84C 0%, #E5C874 100%)" : "#0F6E56" }} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Barra de progreso global dorada */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "rgba(11,61,46,0.08)" }}>
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${answeredPct}%`,
                background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)",
                boxShadow: "0 0 10px rgba(201,168,76,0.55)",
              }}
            />
          </div>
          <div className="text-xs font-bold tabular-nums" style={{ color: "#0b3d2e" }}>{mandatoryAnswered}/{mandatoryQuestions.length}</div>
        </div>

        {/* Pregunta */}
        <div
          className="rounded-3xl p-6 md:p-10 mb-6 relative overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)",
            boxShadow: "0 22px 45px -30px rgba(11,61,46,0.35), 0 0 0 1px rgba(11,61,46,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
          }}
        >
          <span aria-hidden className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: "linear-gradient(90deg, #0b3d2e 0%, #0F6E56 45%, #C9A84C 100%)" }} />
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div
              className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em] px-3 py-1.5 rounded-full"
              style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)", color: "#E5C874", boxShadow: "0 4px 12px -4px rgba(11,61,46,0.35)" }}
            >
              <span style={{ width: 5, height: 5, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
              {areaName}
            </div>
            <div className="text-xs text-slate-500 font-medium">Pregunta {current + 1} de {questions.length}</div>
            {q.optional && (
              <span
                className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full"
                style={{ background: "#fef3c7", color: "#92400e" }}
                data-testid="consulting-question-optional-badge"
              >
                Opcional
              </span>
            )}
          </div>
          <div className="font-display text-lg md:text-xl font-bold text-slate-900 mb-6 leading-snug" style={{ letterSpacing: "-0.01em" }}>
            {q.text}
          </div>

          {q.type === "multiple" ? (
            <div className="space-y-2">
              {q.options.map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setAnswer(q.n, idx, "multiple")}
                  className="group w-full text-left rounded-xl border px-4 py-3 text-sm transition-all"
                  style={{
                    borderColor: value === idx ? "#0F6E56" : "#e2e8f0",
                    background: value === idx ? "linear-gradient(135deg, #ecfdf5 0%, #fefce8 100%)" : "#ffffff",
                    color: "#0f172a",
                    boxShadow: value === idx ? "0 6px 18px -12px rgba(15,110,86,0.35)" : "none",
                  }}
                  data-testid={`consulting-option-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center"
                      style={{ borderColor: value === idx ? "#0F6E56" : "#cbd5e1" }}
                    >
                      {value === idx && <div className="w-2.5 h-2.5 rounded-full" style={{ background: "#0F6E56" }} />}
                    </div>
                    <span className="flex-1">{opt}</span>
                    <kbd
                      className="hidden sm:inline-flex items-center px-1.5 h-5 rounded text-[10px] font-bold tabular-nums flex-shrink-0"
                      style={{ background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0" }}
                    >
                      {idx + 1}
                    </kbd>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div>
              <textarea
                value={value}
                onChange={(e) => setAnswer(q.n, e.target.value, "open")}
                placeholder="Escribe tu respuesta con detalle…"
                rows={7}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm leading-relaxed focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-[#0F6E56]/15 resize-y transition"
                data-testid="consulting-open-input"
              />
              <div className="mt-2 flex items-center justify-between text-[11px]" data-testid="consulting-word-counter">
                <span className={wordCount >= 80 ? "text-emerald-600 font-semibold" : "text-slate-400"}>
                  {wordCount} {wordCount === 1 ? "palabra" : "palabras"}
                  {wordCount >= 80 && " · ¡buen nivel de detalle!"}
                </span>
                <span className="text-slate-400 font-medium">
                  <Sparkles className="w-3 h-3 inline mr-1" style={{ color: "#C9A84C" }} />
                  Más detalle = mejor informe (ideal 80-120 palabras)
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Navegación */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button
            onClick={() => setCurrent((i) => Math.max(0, i - 1))}
            disabled={current === 0}
            className="rounded-full px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 disabled:opacity-30 flex items-center gap-1.5 hover:bg-slate-50 transition"
            data-testid="consulting-prev-btn"
          >
            <ChevronLeft className="w-4 h-4" /> Anterior
          </button>

          <button
            onClick={() => navigate("/consulting")}
            className="text-xs text-slate-500 hover:underline hidden sm:inline"
            data-testid="consulting-save-and-exit"
          >
            Guardar y continuar más tarde
          </button>

          <div className="flex items-center gap-2">
            {mandatoryAnswered === mandatoryQuestions.length && (
              <button
                onClick={() => setReviewOpen(true)}
                className="rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
                style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)", color: "#0b3d2e", boxShadow: "0 8px 22px -8px rgba(201,168,76,0.55)" }}
                data-testid="consulting-review-btn"
              >
                <ListChecks className="w-4 h-4" /> Revisar y enviar
              </button>
            )}
            {current < questions.length - 1 ? (
              <button
                onClick={() => setCurrent((i) => Math.min(questions.length - 1, i + 1))}
                className="rounded-full px-5 py-2 text-sm font-semibold text-white flex items-center gap-1.5 hover:-translate-y-0.5 transition"
                style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)", boxShadow: "0 6px 18px -8px rgba(11,61,46,0.4)" }}
                data-testid="consulting-next-btn"
              >
                Siguiente <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              mandatoryAnswered < mandatoryQuestions.length && (
                <div className="text-xs text-slate-400 font-medium" title={`Responde las ${mandatoryQuestions.length} preguntas obligatorias antes de enviar`}>
                  Faltan {mandatoryQuestions.length - mandatoryAnswered} respuestas
                </div>
              )
            )}
          </div>
        </div>

        {/* Hint de atajos */}
        <div className="mt-6 text-center text-[11px] text-slate-400 font-medium hidden md:block">
          Atajos:{" "}
          <kbd className="mx-1 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "#f1f5f9", border: "1px solid #e2e8f0" }}>←</kbd>{" "}
          <kbd className="mx-1 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "#f1f5f9", border: "1px solid #e2e8f0" }}>→</kbd> navegar ·{" "}
          <kbd className="mx-1 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "#f1f5f9", border: "1px solid #e2e8f0" }}>1-9</kbd> seleccionar opción
        </div>
      </div>

      {/* Modal Revisión pre-submit */}
      {reviewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setReviewOpen(false)}
          data-testid="consulting-review-modal"
        >
          <div
            className="w-full sm:max-w-2xl bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "0 30px 60px -20px rgba(11,61,46,0.45), 0 0 0 1px rgba(201,168,76,0.15)" }}
          >
            <div
              className="relative px-6 py-5 flex items-center justify-between flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
            >
              <span aria-hidden className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }} />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#E5C874" }}>Revisión final</div>
                <div className="text-white font-semibold text-sm mt-0.5">
                  {totalAnswered}/{questions.length} respondidas · {test?.company_name}
                </div>
              </div>
              <button onClick={() => setReviewOpen(false)} className="rounded-full p-1.5 text-white/70 hover:text-white hover:bg-white/10 transition" aria-label="Cerrar" data-testid="consulting-review-close">
                <ChevronRight className="w-5 h-5 rotate-45" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {areaProgress.map((a) => {
                const missing = questions.filter((qq) => qq.area === a.code && (!answers[qq.n] || !String(answers[qq.n].value ?? "").trim()));
                return (
                  <div
                    key={a.code}
                    className="rounded-2xl p-4"
                    style={{
                      background: missing.length === 0 ? "linear-gradient(135deg, rgba(15,110,86,0.06) 0%, rgba(201,168,76,0.05) 100%)" : "#fef7f7",
                      border: `1px solid ${missing.length === 0 ? "rgba(15,110,86,0.20)" : "rgba(244,63,94,0.20)"}`,
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {missing.length === 0 ? (
                        <Check className="w-4 h-4 flex-shrink-0" style={{ color: "#0F6E56" }} strokeWidth={2.5} />
                      ) : (
                        <AlertCircle className="w-4 h-4 flex-shrink-0" style={{ color: "#e11d48" }} strokeWidth={2.25} />
                      )}
                      <span className="font-display font-bold text-slate-900 text-sm">{a.code} · {a.name}</span>
                      <span className="text-[11px] font-bold tabular-nums ml-auto" style={{ color: missing.length === 0 ? "#0F6E56" : "#e11d48" }}>
                        {a.done}/{a.total}
                      </span>
                    </div>
                    {missing.length > 0 && (
                      <div className="space-y-1.5">
                        {missing.map((mq) => (
                          <button
                            key={mq.n}
                            onClick={() => goToQuestion(mq.n)}
                            className="w-full text-left rounded-lg px-3 py-2 text-xs bg-white hover:bg-rose-50 transition"
                            style={{ border: "1px solid rgba(244,63,94,0.15)" }}
                            data-testid={`consulting-review-goto-${mq.n}`}
                          >
                            <span className="text-slate-400 mr-2 font-bold">P{mq.n}</span>
                            <span className="text-slate-700">{mq.text}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="border-t border-slate-100 p-6 flex-shrink-0 flex flex-wrap gap-3 justify-end">
              <button onClick={() => setReviewOpen(false)} className="rounded-full px-5 py-2.5 text-sm font-semibold text-slate-600 border border-slate-300 hover:bg-slate-50 transition">
                Seguir editando
              </button>
              <button
                onClick={goSubmit}
                disabled={submitting || totalAnswered < questions.length}
                className="rounded-full px-6 py-2.5 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50 transition-all hover:-translate-y-0.5"
                style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)", color: "#0b3d2e", boxShadow: "0 8px 22px -8px rgba(201,168,76,0.55)" }}
                data-testid="consulting-review-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={2.5} />}
                {submitting ? "Enviando…" : "Enviar consultoría"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
